---
title: "Automat wykrywa 30% błędów WCAG. Resztę musi znaleźć człowiek."
slug: ai-w-audycie-wcag-co-widzi-automat
date: 2026-03-17
description: "Skanery WCAG wykrywają ok. 30% błędów. Kontekst, logika i zachowanie strony wymagają oceny eksperta. Gdzie kończy się audyt automatyczny?"
tags: [audyt-WCAG, testy-manualne, metodologia, dostępność-cyfrowa]
category: "Metodologia"
readingTime: 5
---

4 na 5 specjalistów dostępności cyfrowej szacuje, że automatyczne narzędzia wyłapują mniej niż połowę błędów WCAG. Największa grupa podaje 20–30%. Jedna trzecia ankietowanych.

To dane z [badania 758 praktyków (WebAIM)](https://webaim.org/projects/practitionersurvey3/) — ludzi, którzy robią audyty WCAG na co dzień. [Analiza Ministerstwa Cyfryzacji z 2025 roku](https://www.gov.pl/web/dostepnosc-cyfrowa/raport--analiza-mozliwosci-wykorzystania-sztucznej-inteligencji-w-obszarze-badania-dostepnosci-cyfrowej) idzie dalej: rekomenduje metodologię hybrydową — skaner plus ekspert.

To nie jest argument przeciwko automatyzacji. To argument za tym, żeby wiedzieć, gdzie w audycie dostępności kończy się maszyna i zaczyna człowiek.

---

## Co skaner audytu WCAG widzi w kilka sekund

Automatyczny skaner w ciągu sekund przejdzie przez każdy element strony. Znajdzie naruszenia, których ręczne sprawdzenie zajęłoby godziny:

- **Obrazy bez opisów** — obraz bez tekstu alternatywnego jest niewidoczny dla czytnika ekranu
- **Przyciski i linki bez nazwy** — czytnik mówi „przycisk", ale nie mówi jaki
- **Formularze bez etykiet** — pole bez opisu, którego przeznaczenie trzeba zgadywać
- **Kontrast kolorów poniżej normy** — tekst nieczytelny dla osób z niedowidzeniem
- **Błędy struktury nagłówków** — np. przeskok z pierwszego na czwarty poziom

{/* TODO: link-internal — post o axe-core i co konkretnie skanuje */}

Skanery takie jak axe-core to dobry punkt wyjścia. Bez nich audyt WCAG zaczyna się od zera.

---

## Czego żaden automat nie wykryje w testach dostępności

Tab. Raz. Czy kursor skoczył do właściwego pola, czy wpadł do stopki?

Skaner sprawdzi, czy elementy reagują na klawiaturę — ale nie sprawdzi kolejności. Nie wykryje, że po wpisaniu imienia fokus ląduje w stopce zamiast w polu nazwiska. Nie sprawdzi, czy okno dialogowe da się zamknąć klawiszem Escape. Nie zauważy, że kursor gubi się w połowie formularza i nie ma jak wrócić. To wykryją testy manualne — nie skrypt.

Obraz z atrybutem `alt="logo"` technicznie spełnia wymaganie — skaner nie zgłosi błędu. Ale jeśli ten obraz jest jednocześnie linkiem do strony głównej, "logo" to za mało. Powinno być: "Urząd Gminy Przykład — strona główna". Albo odwrotnie: obraz dekoracyjny w tle powinien mieć pusty opis, żeby czytnik go pominął. Żadna z tych decyzji nie należy do automatycznego narzędzia.

72 razy. Tyle razy użytkownik czytnika ekranu słyszał "napisy niedostępne" na jednej stronie z filmami — i żadnego błędu w raporcie skanera. Narzędzie sprawdzi, czy film ma dołączony plik napisów. Nie sprawdzi, czy napisy są zsynchronizowane, czy opisują dźwięki pozawerbalne — syrenę, brawa, ciszę — ani czy są czytelne na tle zmieniającego się obrazu. Plik jest? Skaner jest zadowolony. Użytkownik — niekoniecznie.

Porównaj dwa scenariusze: formularz z komunikatem "Pole wymagane" i formularz z komunikatem "Pole wymagane: Imię". Skaner zaakceptuje oba. Użytkownik z czytnikiem ekranu ma szansę tylko z jednym. Zrozumiałość strony to obszar testów manualnych — skaner nie oceni, czy komunikat błędu wskazuje konkretne pole, czy strona daje wystarczający czas przed wygaśnięciem sesji, czy wieloetapowy formularz jasno pokazuje aktualny krok.

Sprawdź jedną liczbę: [9 na 10 użytkowników czytnika ekranu](https://webaim.org/projects/screenreadersurvey10/) korzysta z niego na telefonie. Skaner testuje kod. Nie testuje, jak strona zachowuje się na iPhonie z VoiceOver ani na Androidzie z TalkBack. Gesty dotykowe, powiększanie, orientacja ekranu — to testy, które musi wykonać człowiek.

---

## Jak ta różnica wpływa na audyt WCAG w praktyce

Dwa audyty tej samej strony dadzą różne wyniki — i to nie znaczy, że jeden jest błędny. Zależy od zakresu.

Audyt oparty wyłącznie na skanerze zwróci błędy wykrywalne w kodzie. Szybki. Tani. Za mało, żeby ocenić zgodność z ustawą. Za mało, żeby napisać wiarygodną deklarację dostępności.

Audyt z testami manualnymi — klawiatura, czytnik ekranu, formularz od początku do końca — pokaże kontekst, logikę i zachowanie strony. Znajdzie resztę błędów, tę której skaner nie widzi.

{/* TODO: link-internal — post o deklaracji dostępności i co musi zawierać */}

Metodologia ma znaczenie. Szybko wyprodukowany raport i rzetelny audyt dostępności to dwie różne usługi.

---

## Jak sprawdzić, czy zamawiasz audyt WCAG czy tylko skan?

Zapytaj wprost: czy w zakres audytu wchodzą testy manualne z klawiaturą i czytnikiem ekranu?

Odpowiedź wymijająca albo brak odpowiedzi — to już informacja.

---

{/* CTA — wydzielony element wizualny */}
{/* Sprawdź jak Twoja strona wypada w audycie dostępności. Wyślij adres na kontakt@bastunit.pl — wrócimy z wstępną diagnozą. */}
